package com.downdogapp.client

import com.downdogapp.*
import org.w3c.dom.*

class JsMediaPlayer(isVideo: Boolean = true,
                    var loop: Boolean = false,
                    private val printLogs: Boolean = true) : MediaPlayer {

  private var element: HTMLMediaElement = if (isVideo) createVideo { playsInline = true } else createAudio()

  override val duration
    get() =
      seconds(
        element.duration.let {
          if (it.isFinite() && it > 0.0)
            it
          else
            element.seekable.let {
              if (it.length == 0)
                0.0
              else
                it.end(it.length - 1)
            }
        }
      )

  // We tried readyState, but that would switch out of 4 as the player advanced with a stutter.
  // Using the buffer ranges also didn't work because sometimes a frame be out of the ranges,
  // although everything was loaded around it.
  private var internalCanPlay = false

  private var file: String? = null
  private var completed = false
  private var seekTimeOnMetaData: Duration? = null
  private var onPlaybackFinished: (() -> Unit)? = null
  private var hlsObj: dynamic = null

  private fun print(string: String) {
    if (printLogs) {
      println(string)
    }
  }

  init {
    element.onloadedmetadata = {
      print("onloadedmetadata")
      seekTimeOnMetaData?.let {
        seekToTime(it)
        seekTimeOnMetaData = null
      }
    }
    element.onended = {
      print("onended")
      if (this@JsMediaPlayer.loop) {
        play()
      } else {
        completed = true
        internalCanPlay = true
      }
      onPlaybackFinished?.invoke()
    }
    element.oncanplay = {
      print("oncanplay")
      internalCanPlay = true
      null
    }
    element.onwaiting = {
      print("onwaiting")
    }
    element.onstalled = {
      print("onstalled")
    }
    element.ondurationchange = {
      print("ondurationchange")
    }
    element.oncancel = {
      print("oncancel")
    }
    element.onabort = {
      print("onabort")
    }
    element.onsuspend = {
      print("onsuspend")
    }
    element.onerror = { a, b, c, d, e ->
      print("onerror $a $b $c $d $e")
    }
    element.onemptied = {
      print("onemptied")
    }

    if (isVideo) {
      element.apply {
        style.width = "inherit"
        style.height = "inherit"
      }
    }
  }

  override val isPlaying get() = !element.paused
  override val canPlay get() = internalCanPlay
  private val playerTime get() = seconds(element.currentTime as? Double ?: 0.0)
  override val currentTime get() = seekTimeOnMetaData ?: playerTime

  override var volume: Double
    get() = element.volume
    set(value) {
      element.volume = value
    }

  override fun setFile(file: CommonUri?, seekTime: Duration?) {
    setFileInternal(file?.path)
    seekTime?.let {
      seekToTime(it)
    }
  }

  private fun setFileInternal(src: String?) {
    if (src != null && src == this.file) {
      return
    }
    seekTimeOnMetaData = null

    this.file = src
    if (src == null) {
      pause()
      element.src = ""
    } else {
      internalCanPlay = false
      if (src.endsWith(".m3u8")
          && element.canPlayType("application/vnd.apple.mpegurl") == CanPlayTypeResult.Companion.EMPTY) {
        hlsObj?.destroy()
        hlsObj = js("new Hls()")
        hlsObj.loadSource(src)
        hlsObj.attachMedia(element)
      } else {
        element.src = src
      }
      element.load()
    }
  }

  override fun seekToTime(time: Duration) {
    if (file == null) {
      return
    }
    if (element.readyState == 0.toShort()) {
      seekTimeOnMetaData = time
      return
    }
    if (playerTime == time) {
      return
    }

    if (time < duration) {
      completed = false
      internalCanPlay = false
      element.currentTime = time.seconds
    } else {
      completed = true
    }
  }

  override fun play() {
    if (file == null || (completed && !loop)) {
      return
    }
    element
      .play()
      .catch {
        println(it)
        /* If pause/play async interrupt each other, fail silently (no observable affect yet) */
      }
  }

  override fun pause() {
    element.pause()
  }

  override fun releasePlayer() {
    element.pause()
    element.remove()
    hlsObj?.destroy()
  }

  override fun onFinishedPlaying(callback: () -> Unit) {
    onPlaybackFinished = callback
  }

  fun onSeeked(callback: JsMediaPlayer.() -> Unit) {
    element.onseeked = {
      callback.invoke(this)
    }
  }

  fun onPause(callback: JsMediaPlayer.() -> Unit) {
    element.onpause = {
      callback.invoke(this)
    }
  }

  fun onTimeChange(callback: JsMediaPlayer.() -> Unit) {
    element.ontimeupdate = {
      callback.invoke(this)
    }
  }

  fun addToView(view: HTMLElement) {
    view.appendChild(element)
  }

  fun show() {
    element.show()
  }

  fun hide() {
    element.hide()
  }

  fun applyToElement(block: HTMLMediaElement.() -> Unit): JsMediaPlayer {
    element.block()
    return this
  }

  companion object {

    fun HTMLElement.jsMediaPlayer(isVideo: Boolean = true,
                                  loop: Boolean = false,
                                  block: HTMLMediaElement.() -> Unit = {}) =
      JsMediaPlayer(
        isVideo = isVideo,
        loop = loop
      ).also {
        it.applyToElement(block)
        it.addToView(this)
      }
  }
}
